/**
 * +grid: the Waystone Grid's character state and economy (docs/design/waystone-grid.md "Progression").
 * Pure: every op returns a new Profile and never mutates its input; no clock, no RNG.
 *
 *  - Points: GRID_POINTS_PER_LEVEL per level after the first (27 at level 10) plus one per finished
 *    main-quest milestone (content GRID_POINT_STEPS: 5 in Mossvale), all derived from the profile.
 *  - Grid Keys: one per source (content GRID_KEY_SOURCES). Quest steps, side quests, dungeon first
 *    clears and the Tower are derived from the profile; rare kills and secret chests are logged in
 *    `grid.keyLog` when they happen (server-side). Keys held = earned − locks opened.
 *  - Allocation: a star must touch the class gate, an allocated star or an opened lock that does;
 *    each costs 1 point. Opening a lock costs a key (and no point) and lasts forever.
 *  - Refunds keep the rest connected. Free below GRID_RESPEC_LEVEL, then GRID_RESPEC_GOLD per point.
 *  - The suggested path (suggestedGrid) is a deterministic greedy planner over role-weighted values;
 *    Group Finder strangers, bots and the harness use it, and so does the Suggested button.
 */
import type { ClassDef, ClassId, Role } from '@keystone/sim';
import {
  GRID_KEY_SOURCES,
  GRID_NOTABLES,
  GRID_POINT_STEPS,
  GRID_STAT_VALUE,
  type GridIdle,
  type GridNode,
  type GridStat,
  getGrid,
  gridGateOf,
  specDef,
} from '@keystone/content';
import { zoneQuests } from './bounties';
import { slotAbility } from './grid.compile';
import type { CharacterProgress, GridState, HeroCharacter, Profile } from './types';

export const GRID_POINTS_PER_LEVEL = 3;
/** At or above this level a refund costs gold. */
export const GRID_RESPEC_LEVEL = 10;
export const GRID_RESPEC_GOLD = 2;
/** How close (yards) the hero must stand to a key-bearing secret chest to claim its key. */
export const GRID_SECRET_YD = 12;

export const emptyGridState = (): GridState => ({ nodes: [], locks: [], keyLog: [] });

export function gridStateOf(ch: Pick<CharacterProgress, 'grid'> | undefined | null): GridState {
  const g = ch?.grid;
  return { nodes: [...(g?.nodes ?? [])], locks: [...(g?.locks ?? [])], keyLog: [...(g?.keyLog ?? [])] };
}

// ---------------------------------------------------------------------------
// Points
// ---------------------------------------------------------------------------

/** Points from levels: 3 per level after the first. */
export const gridLevelPoints = (level: number): number => GRID_POINTS_PER_LEVEL * Math.max(0, Math.floor(level || 1) - 1);

/** Finished main-quest steps (any zone) by id. */
function finishedSteps(hero: Pick<HeroCharacter, 'bounties'> | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const [zoneId, idx] of Object.entries(hero?.bounties?.chain ?? {})) {
    const chain = zoneQuests(zoneId)?.chain ?? [];
    for (let i = 0; i < Math.min(idx, chain.length); i++) out.add(chain[i]!.id);
  }
  return out;
}

/** Points from main-quest milestones. */
export function gridQuestPoints(hero: Pick<HeroCharacter, 'bounties'> | null | undefined): number {
  const done = finishedSteps(hero);
  return GRID_POINT_STEPS.filter((id) => done.has(id)).length;
}

/** Milestone points a Group Finder stranger / bot of `level` is assumed to have (all five by 10). */
export const gridQuestPointsAt = (level: number): number => Math.min(GRID_POINT_STEPS.length, Math.max(0, Math.floor(((level || 1) - 1) * GRID_POINT_STEPS.length / 9)));

/** Total points a bot / stranger / harness hero of `level` has. */
export const gridPointsAt = (level: number): number => gridLevelPoints(level) + gridQuestPointsAt(level);

export interface GridPoints {
  total: number;
  fromLevels: number;
  fromQuests: number;
  spent: number;
  free: number;
}

export function gridPoints(p: Profile, classId: ClassId | undefined = p.character?.classId): GridPoints {
  const ch = classId ? p.characters[classId] : undefined;
  const fromLevels = gridLevelPoints(ch?.hero.level ?? 1);
  const fromQuests = p.character && p.character.classId === classId ? gridQuestPoints(p.character) : 0;
  const spent = ch?.grid?.nodes.length ?? 0;
  const total = fromLevels + fromQuests;
  return { total, fromLevels, fromQuests, spent, free: Math.max(0, total - spent) };
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

/** Every key source this character has earned (stable order: derived first, then the log). */
export function gridKeySources(p: Profile, classId: ClassId | undefined = p.character?.classId): string[] {
  const out: string[] = [];
  const hero = p.character && p.character.classId === classId ? p.character : null;
  const ch = classId ? p.characters[classId] : undefined;
  if (hero) {
    const steps = finishedSteps(hero);
    for (const s of GRID_KEY_SOURCES.steps) if (steps.has(s)) out.push(`step:${s}`);
    for (const q of GRID_KEY_SOURCES.quests) if ((hero.quests?.done[q] ?? 0) > 0) out.push(`quest:${q}`);
    for (const d of GRID_KEY_SOURCES.dungeons) if ((hero.dungeonClears[d] ?? 0) > 0) out.push(`dungeon:${d}`);
  }
  if (GRID_KEY_SOURCES.tower && ch?.hero.specId) out.push('tower');
  for (const k of ch?.grid?.keyLog ?? []) if (!out.includes(k)) out.push(k);
  return out;
}

export interface GridKeys {
  earned: number;
  spent: number;
  held: number;
  sources: string[];
}

export function gridKeys(p: Profile, classId: ClassId | undefined = p.character?.classId): GridKeys {
  const sources = gridKeySources(p, classId);
  const spent = (classId ? p.characters[classId]?.grid?.locks.length : 0) ?? 0;
  return { earned: sources.length, spent, held: Math.max(0, sources.length - spent), sources };
}

/** Log an event key source on the hero's character (in place; false when already claimed / not a source). */
export function logGridKey(ch: CharacterProgress, source: string): boolean {
  const valid = (source.startsWith('rare:') && GRID_KEY_SOURCES.rares.includes(source.slice(5))) || (source.startsWith('secret:') && GRID_KEY_SOURCES.secrets.includes(source.slice(7)));
  if (!valid) return false;
  const g = (ch.grid ??= emptyGridState());
  g.keyLog ??= [];
  if (g.keyLog.includes(source)) return false;
  g.keyLog.push(source);
  return true;
}

// ---------------------------------------------------------------------------
// The graph rules
// ---------------------------------------------------------------------------

const ALLOCATABLE = new Set(['gate', 'minor', 'slot', 'notable', 'utility', 'keystone']);

/** The connected allocation: nodes reachable from the gate through allocated stars and opened locks. */
export function gridReach(classId: ClassId, state: Pick<GridState, 'nodes' | 'locks'>): Set<string> {
  const g = getGrid();
  const gate = gridGateOf(classId);
  const out = new Set<string>();
  if (!gate) return out;
  const allowed = new Set([...state.nodes, ...state.locks]);
  out.add(gate);
  const q = [gate];
  while (q.length) {
    const id = q.shift()!;
    for (const l of g.byId.get(id)?.links ?? []) {
      if (out.has(l) || !allowed.has(l)) continue;
      out.add(l);
      q.push(l);
    }
  }
  return out;
}

const touches = (n: GridNode, reach: ReadonlySet<string>): boolean => n.links.some((l) => reach.has(l));

/** Why `nodeId` can't be allocated next (null = it can). Points are the caller's. */
export function gridAllocBlocker(classId: ClassId, state: Pick<GridState, 'nodes' | 'locks'>, nodeId: string): string | null {
  const n = getGrid().byId.get(nodeId);
  if (!n) return 'Unknown star';
  if (!gridGateOf(classId)) return 'This class has no gate';
  if (n.type === 'dormant') return 'That star has not formed yet';
  if (n.type === 'lock') return 'Open the seal with a Grid Key';
  if (!ALLOCATABLE.has(n.type)) return 'Not a star';
  if (nodeId === gridGateOf(classId)) return 'Your gate is already yours';
  if (state.nodes.includes(nodeId)) return 'Already allocated';
  if (!touches(n, gridReach(classId, state))) return 'Not connected to your stars';
  return null;
}

/** Why the lock can't be opened (keys are the caller's; null = it can). */
export function gridUnlockBlocker(classId: ClassId, state: Pick<GridState, 'nodes' | 'locks'>, lockId: string): string | null {
  const n = getGrid().byId.get(lockId);
  if (!n || n.type !== 'lock') return 'Not a seal';
  if (state.locks.includes(lockId)) return 'Already open';
  if (!touches(n, gridReach(classId, state))) return 'Reach the seal first';
  return null;
}

/** Why `nodeId` can't be refunded (null = it can): the rest must stay connected. */
export function gridRefundBlocker(classId: ClassId, state: Pick<GridState, 'nodes' | 'locks'>, nodeId: string): string | null {
  if (!state.nodes.includes(nodeId)) return 'Not allocated';
  const rest = { nodes: state.nodes.filter((x) => x !== nodeId), locks: state.locks };
  const reach = gridReach(classId, rest);
  if (rest.nodes.some((x) => !reach.has(x))) return 'Other stars depend on it';
  return null;
}

/**
 * The usable part of a saved allocation: unknown / unallocatable ids dropped, then only stars that
 * connect (in saved order, repeating passes), within `points`. Locks stay (they were paid for) when
 * they are real lock ids. Never throws: an old save always loads.
 */
export function sanitizeGrid(classId: ClassId, state: GridState | undefined, points: number): GridState {
  const g = getGrid();
  const s = state ?? emptyGridState();
  const locks = [...new Set((s.locks ?? []).filter((id) => g.byId.get(id)?.type === 'lock'))];
  const keyLog = [...new Set((s.keyLog ?? []).filter((x) => typeof x === 'string'))];
  const want = [...new Set((s.nodes ?? []).filter((id) => typeof id === 'string'))];
  const nodes: string[] = [];
  for (let changed = true; changed && nodes.length < points; ) {
    changed = false;
    for (const id of want) {
      if (nodes.length >= points) break;
      if (nodes.includes(id)) continue;
      if (gridAllocBlocker(classId, { nodes, locks }, id) === null) {
        nodes.push(id);
        changed = true;
      }
    }
  }
  return { nodes, locks, keyLog };
}

// ---------------------------------------------------------------------------
// The suggested path
// ---------------------------------------------------------------------------

/** Value of one inner minor of each stat, by role (1 = a good minor for that role). */
const ROLE_STAT: Readonly<Record<Role, Readonly<Record<GridStat, number>>>> = {
  dps: { damage: 1, crit: 1, haste: 1, maxHp: 0.45, armor: 0.3, dodge: 0.3, healing: 0.1, regen: 0.55, cooldown: 0.6, leech: 0.5, healingTaken: 0.3, moveSpeed: 0.3 },
  tank: { armor: 1, dodge: 0.9, maxHp: 0.75, healingTaken: 0.6, damage: 0.65, crit: 0.45, haste: 0.65, leech: 0.8, regen: 0.5, cooldown: 0.7, healing: 0.2, moveSpeed: 0.3 },
  healer: { healing: 1, haste: 0.9, regen: 0.9, cooldown: 0.7, maxHp: 0.6, crit: 0.5, damage: 0.3, healingTaken: 0.4, armor: 0.3, dodge: 0.3, leech: 0.2, moveSpeed: 0.3 },
};
const SLOT_VALUE = 1.3;

/**
 * Per-spec planner bias (×value; 0 = never): a spec whose mechanic fights a star skips it. The
 * Hemomancer pays health to heal and is strongest hurt, so self-heals and health stars are off its path.
 */
const SPEC_BIAS: Readonly<Record<string, { notables?: Readonly<Record<string, number>>; stats?: Partial<Record<GridStat, number>>; idle?: Partial<Record<GridIdle, number>> }>> = {
  // The Rimecaller's frost kit scales with damage and crit far more than with haste (solo bench).
  rimecaller: { stats: { damage: 1.5, crit: 1.3, haste: 0.5 } },
  hemomancer: { notables: { kindred_light: 0, hearth_pulse: 0, rekindle: 0, bulwark_psalm: 0, grindstone: 0 }, stats: { maxHp: 0, healingTaken: 0, leech: 0, healing: 0.3 } },
};
const UTILITY_VALUE = 0.35;
/** How far (new stars) the planner looks for its next target. */
const PLAN_REACH = 9;
/** Planned budget the suggested order is computed for (later rings: raise with the cap). */
const PLAN_BUDGET = 60;

function nodeValue(n: GridNode, role: Role, def: ClassDef | undefined): number {
  let v = 0;
  const bias = def?.specId ? SPEC_BIAS[def.specId] : undefined;
  for (const e of n.effects) {
    if (e.kind === 'stat') v += ROLE_STAT[role][e.stat] * (e.value / GRID_STAT_VALUE[e.stat]) * (bias?.stats?.[e.stat] ?? 1);
    else if (e.kind === 'idle') v += UTILITY_VALUE * (bias?.idle?.[e.stat] ?? 1);
    else if (e.kind === 'slot') v += def && !slotAbility(def, e.sel) ? 0 : SLOT_VALUE;
    else if (e.kind === 'notable') v += (GRID_NOTABLES.find((x) => x.id === e.id)?.value[role] ?? 1) * (bias?.notables?.[e.id] ?? 1);
    // keystones: never auto-picked (a deliberate choice); value 0 keeps the planner off them
  }
  return v;
}

/**
 * Greedy planner: from what is allocated, repeatedly take the path (≤ PLAN_REACH new stars, through
 * opened locks only) with the best value per point; ties → shorter path, then the target id. Returns
 * the new stars in allocation order (each touches the ones before it). Deterministic.
 */
export function planGrid(classId: ClassId, role: Role, budget: number, start: Pick<GridState, 'nodes' | 'locks'> = { nodes: [], locks: [] }, def?: ClassDef): string[] {
  const g = getGrid();
  const gate = gridGateOf(classId);
  if (!gate || budget <= 0) return [];
  const allocated = new Set(start.nodes);
  const locks = new Set(start.locks);
  const picked: string[] = [];
  const value = new Map<string, number>();
  const valueOf = (n: GridNode): number => {
    let v = value.get(n.id);
    if (v === undefined) value.set(n.id, (v = n.type === 'gate' || n.type === 'keystone' ? 0 : nodeValue(n, role, def)));
    return v;
  };
  let left = budget;
  while (left > 0) {
    const reach = gridReach(classId, { nodes: [...allocated], locks: [...locks] });
    // BFS over unallocated stars from the reach (opened locks are free to pass, sealed ones block).
    const parent = new Map<string, string | null>();
    const depth = new Map<string, number>();
    const gain = new Map<string, number>();
    const q: string[] = [];
    for (const id of [...reach].sort()) {
      parent.set(id, null);
      depth.set(id, 0);
      gain.set(id, 0);
      q.push(id);
    }
    let best: { id: string; score: number; len: number } | null = null;
    while (q.length) {
      const id = q.shift()!;
      const d = depth.get(id)!;
      for (const l of g.byId.get(id)?.links ?? []) {
        if (parent.has(l)) continue;
        const n = g.byId.get(l);
        if (!n || n.type === 'dormant' || (n.type === 'lock' && !locks.has(l)) || !ALLOCATABLE.has(n.type) && n.type !== 'lock') continue;
        const step = n.type === 'lock' ? 0 : 1;
        const nd = d + step;
        if (nd > Math.min(PLAN_REACH, left)) continue;
        parent.set(l, id);
        depth.set(l, nd);
        const gv = gain.get(id)! + (step ? valueOf(n) : 0);
        gain.set(l, gv);
        q.push(l);
        if (step && n.type !== 'keystone' && n.type !== 'gate') {
          const score = gv / nd;
          if (!best || score > best.score + 1e-9 || (Math.abs(score - best.score) <= 1e-9 && (nd < best.len || (nd === best.len && l < best.id)))) best = { id: l, score, len: nd };
        }
      }
    }
    if (!best || best.score <= 0) break;
    // Walk the path back to the reach.
    const path: string[] = [];
    for (let at: string | null = best.id; at && !reach.has(at); at = parent.get(at) ?? null) path.unshift(at);
    for (const id of path) {
      if (g.byId.get(id)?.type === 'lock') continue;
      if (left <= 0) break;
      allocated.add(id);
      picked.push(id);
      left--;
    }
  }
  return picked;
}

const planCache = new Map<string, readonly string[]>();

/**
 * The suggested allocation for a class (role: the spec's when given) at `points`: the first `points`
 * stars of one long greedy plan, so a bigger budget always extends a smaller one (a levelling bot
 * never re-routes). `locks` lets the plan walk through opened seals.
 */
export function suggestedGrid(classId: ClassId, role: Role, points: number, locks: readonly string[] = [], def?: ClassDef): string[] {
  const key = `${classId}|${role}|${[...locks].sort().join(',')}|${def?.id ?? ''}|${def?.specId ?? ''}`;
  let plan = planCache.get(key);
  if (!plan) {
    plan = planGrid(classId, role, PLAN_BUDGET, { nodes: [], locks: [...locks] }, def);
    planCache.set(key, plan);
  }
  return plan.slice(0, Math.max(0, Math.floor(points)));
}

/** Role of a (possibly spec-keyed) class for the planner. */
export function gridRoleOf(def: Pick<ClassDef, 'role' | 'specId'>): Role {
  return specDef(def.specId ?? null)?.role ?? def.role;
}
